import { Season } from '../types';

class SeasonService {
    private seasons: Season[] = [
        { id: 'peak', name: 'Peak', startMonth: 7, startDay: 1, endMonth: 10, endDay: 31, priceMultiplier: 1.5 },
        { id: 'high', name: 'High', startMonth: 1, startDay: 1, endMonth: 3, endDay: 31, priceMultiplier: 1.2 },
        { id: 'shoulder', name: 'High', startMonth: 11, startDay: 1, endMonth: 12, endDay: 31, priceMultiplier: 1.2 },
        { id: 'low', name: 'Low', startMonth: 4, startDay: 1, endMonth: 6, endDay: 30, priceMultiplier: 0.8 },
    ];

    /**
     * Get season for a specific date
     * Accuracy: 1 month granularity
     */
    getSeasonByDate(date: Date): Season {
        const month = date.getMonth() + 1; // 1-12
        const day = date.getDate();
        const dateScore = month * 100 + day;

        const matchingSeasons = this.seasons.filter(s => {
            const startScore = s.startMonth * 100 + (s.startDay || 1);
            const endScore = s.endMonth * 100 + (s.endDay || 31);

            if (startScore <= endScore) {
                return dateScore >= startScore && dateScore <= endScore;
            } else {
                return dateScore >= startScore || dateScore <= endScore;
            }
        });

        if (matchingSeasons.length === 0) {
            // Fallback to a neutral season if none found
            return { id: 'standard', name: 'High', startMonth: 1, startDay: 1, endMonth: 12, endDay: 31, priceMultiplier: 1.0 };
        }

        // Safety rule: always choose the most expensive overlapping season
        return matchingSeasons.reduce((prev, curr) =>
            curr.priceMultiplier > prev.priceMultiplier ? curr : prev
        );
    }

    /**
     * Resolve the authoritative season for a date range
     * Safety: Picks the highest multiplier in the range to prevent underpricing
     */
    resolveSeasonForRange(startDate: string, endDate: string): Season {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // If range is within 1 month, just check start
        if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
            return this.getSeasonByDate(start);
        }

        // Check all months in range
        let current = new Date(start);
        const resolvedSeasons: Season[] = [];

        // Limit to 24 months to prevent infinite loops on bad data
        let safetyCounter = 0;
        while (current <= end && safetyCounter < 24) {
            resolvedSeasons.push(this.getSeasonByDate(current));
            current.setMonth(current.getMonth() + 1);
            safetyCounter++;
        }

        if (resolvedSeasons.length === 0) return this.getSeasonByDate(start);

        // Safety rule: Pick the peak season in the travel window
        return resolvedSeasons.reduce((prev, curr) =>
            curr.priceMultiplier > prev.priceMultiplier ? curr : prev
        );
    }

    getAllSeasons(): Season[] {
        return this.seasons;
    }
}

export const seasonService = new SeasonService();
export default seasonService;
